// Web Audio API Sound Synthesizer & Music Engine for GamifyCenter
// Zero external asset dependencies - generates realistic arcade audio effects natively in any browser
// Also supports custom audio URL streaming for background music.

let audioCtx = null;
let currentBgmAudio = null;
let proceduralBgmInterval = null;
let proceduralBgmGain = null;
let isBgmActive = false;
let globalSoundVolume = 0.7;
let globalMusicVolume = 0.35;

export const getAudioContext = () => {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
};

// Set volumes globally
export const setAudioVolumes = ({ sfxVolume, musicVolume }) => {
  if (typeof sfxVolume === 'number') globalSoundVolume = Math.max(0, Math.min(1, sfxVolume));
  if (typeof musicVolume === 'number') {
    globalMusicVolume = Math.max(0, Math.min(1, musicVolume));
    if (currentBgmAudio) currentBgmAudio.volume = globalMusicVolume;
    if (proceduralBgmGain && audioCtx) {
      proceduralBgmGain.gain.setValueAtTime(globalMusicVolume * 0.15, audioCtx.currentTime);
    }
  }
};

// --- Basic Utility Sounds ---

export const playTickSound = (frequency = 800, duration = 0.04) => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(frequency * 0.5, ctx.currentTime + duration);

    gain.gain.setValueAtTime(0.12 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch (e) {
    console.debug('Audio play failed', e);
  }
};

// --- Slot Machine Sounds ---

// Sound 1: Arcade Lever Pull / Big Spin Pushbutton Clack & Spring
export const playLeverPullSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    // Heavy mechanical clank (triangle wave with pitch drop)
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(320, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.12);

    gain.gain.setValueAtTime(0.25 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.14);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.14);

    // Spring resonance
    setTimeout(() => {
      try {
        const spring = ctx.createOscillator();
        const sGain = ctx.createGain();
        spring.type = 'sine';
        spring.frequency.setValueAtTime(450, ctx.currentTime);
        spring.frequency.exponentialRampToValueAtTime(150, ctx.currentTime + 0.2);
        sGain.gain.setValueAtTime(0.1 * globalSoundVolume, ctx.currentTime);
        sGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
        spring.connect(sGain);
        sGain.connect(ctx.destination);
        spring.start();
        spring.stop(ctx.currentTime + 0.2);
      } catch (err) {}
    }, 70);
  } catch (e) {
    console.debug('Lever audio error', e);
  }
};

// Sound 2: Continuous Mechanical Reel Spinning Sound
export const playReelSpinSound = (durationMs = 2500) => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return () => {};

    let isRunning = true;
    let clickDelay = 45;
    const startTime = Date.now();

    const spinStep = () => {
      if (!isRunning) return;
      const elapsed = Date.now() - startTime;
      if (elapsed >= durationMs) return;

      // Realistic mechanical wheel clatter
      playTickSound(350 + Math.random() * 250, 0.035);

      setTimeout(spinStep, clickDelay);
    };

    spinStep();

    return () => { isRunning = false; };
  } catch (e) {
    console.debug('Reel spin audio error', e);
    return () => {};
  }
};

// Sound 3: Reel Sudden Lock / Stop Clunk
export const playReelStopSound = (reelIndex = 1) => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    // Pitch rises slightly per reel (1st = low, 2nd = mid, 3rd = high impact)
    const baseFreq = reelIndex === 1 ? 180 : reelIndex === 2 ? 240 : 340;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(baseFreq, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.1);

    gain.gain.setValueAtTime(0.3 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.12);

    // Bell chime on 3rd reel stop
    if (reelIndex === 3) {
      setTimeout(() => {
        playTickSound(880, 0.1);
      }, 50);
    }
  } catch (e) {
    console.debug('Reel stop audio error', e);
  }
};

// Sound 4: Vegas Slot Jackpot & Coin Shower
export const playJackpotSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    // Fast cascade of bright coin tones
    const coinPitches = [987.77, 1174.66, 1318.51, 1567.98, 1760.00, 2093.00, 2349.32];
    coinPitches.forEach((pitch, i) => {
      setTimeout(() => {
        try {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(pitch, ctx.currentTime);
          gain.gain.setValueAtTime(0.18 * globalSoundVolume, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start();
          osc.stop(ctx.currentTime + 0.25);
        } catch (err) {}
      }, i * 65);
    });

    // Follow with celebratory fanfare
    setTimeout(() => {
      playWinFanfare();
    }, 450);
  } catch (e) {
    console.debug('Jackpot audio error', e);
  }
};

// Sound 5: Subtle "Better Luck Next Time" descending chime
export const playLoseSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const notes = [
      { freq: 440, delay: 0 },
      { freq: 392, delay: 180 },
      { freq: 349.23, delay: 380 },
      { freq: 293.66, delay: 600 }
    ];

    notes.forEach(n => {
      setTimeout(() => {
        try {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(n.freq, ctx.currentTime);
          gain.gain.setValueAtTime(0.15 * globalSoundVolume, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.28);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start();
          osc.stop(ctx.currentTime + 0.28);
        } catch (err) {}
      }, n.delay);
    });
  } catch (e) {
    console.debug('Lose sound error', e);
  }
};

// --- Spin Wheel Sounds ---

// Pointer click when a peg passes the flapper
export const playPegTickSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(950, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.03);

    gain.gain.setValueAtTime(0.2 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.035);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.035);
  } catch (e) {
    console.debug('Peg tick audio error', e);
  }
};

// Realistic wheel spin with natural physics deceleration clicks
export const playWheelSpinSound = (durationMs = 5000) => {
  try {
    let isCancelled = false;
    let currentInterval = 40; // Fast initial ticks
    const startTime = Date.now();

    const nextTick = () => {
      if (isCancelled) return;
      const elapsed = Date.now() - startTime;
      if (elapsed >= durationMs) return;

      playPegTickSound();

      // Non-linear deceleration: ticks spread out as wheel slows down
      const progress = elapsed / durationMs;
      currentInterval = 40 + Math.pow(progress, 2.4) * 450;

      setTimeout(nextTick, currentInterval);
    };

    nextTick();

    return () => { isCancelled = true; };
  } catch (e) {
    console.debug('Wheel spin error', e);
    return () => {};
  }
};

// --- Scratch, Peel & Flip Sounds ---

export const playScratchSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    // Buffer noise burst for scratch texture
    const bufferSize = ctx.sampleRate * 0.08;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1800;
    filter.Q.value = 3.0;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.08 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    noise.start();
  } catch (e) {
    console.debug('Scratch audio error', e);
  }
};

export const playPeelSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(400, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.18);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 800;

    gain.gain.setValueAtTime(0.12 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.2);
  } catch (e) {
    console.debug('Peel audio error', e);
  }
};

export const playFlipSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(250, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.08);

    gain.gain.setValueAtTime(0.2 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.14);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.14);
  } catch (e) {
    console.debug('Flip audio error', e);
  }
};

// Balloon pop sound effect
export const playPopSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    // Pop low-frequency pressure wave
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(450, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(40, ctx.currentTime + 0.08);

    gain.gain.setValueAtTime(0.3 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.09);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.09);
  } catch (e) {
    console.debug('Pop audio error', e);
  }
};

// Quiz correct answer chime (High pleasant double chime)
export const playCorrectChime = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const notes = [
      { freq: 880, delay: 0, dur: 0.15 },
      { freq: 1318.5, delay: 0.1, dur: 0.35 }
    ];

    notes.forEach(n => {
      setTimeout(() => {
        try {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(n.freq, ctx.currentTime);
          gain.gain.setValueAtTime(0.25 * globalSoundVolume, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + n.dur);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start();
          osc.stop(ctx.currentTime + n.dur);
        } catch (err) {}
      }, n.delay * 1000);
    });
  } catch (e) {
    console.debug('Correct chime audio error', e);
  }
};

// Quiz wrong answer buzzer (Low abrasive saw buzzer)
export const playWrongBuzzer = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(140, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(120, ctx.currentTime + 0.35);

    gain.gain.setValueAtTime(0.25 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.38);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.38);
  } catch (e) {
    console.debug('Wrong buzzer audio error', e);
  }
};

// Celebratory victory fanfare
export const playWinFanfare = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    // Major chord arpeggio: C5 (523Hz), E5 (659Hz), G5 (784Hz), C6 (1046Hz)
    const notes = [
      { freq: 523.25, time: 0, dur: 0.15 },
      { freq: 659.25, time: 0.12, dur: 0.15 },
      { freq: 783.99, time: 0.24, dur: 0.2 },
      { freq: 1046.50, time: 0.40, dur: 0.6 }
    ];

    notes.forEach(note => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(note.freq, ctx.currentTime + note.time);

      gain.gain.setValueAtTime(0.25 * globalSoundVolume, ctx.currentTime + note.time);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + note.time + note.dur);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime + note.time);
      osc.stop(ctx.currentTime + note.time + note.dur);
    });
  } catch (e) {
    console.debug('Win fanfare audio error', e);
  }
};

// Kids Piñata Hit / Bonk Sound
export const playPinataHitSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(320, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(90, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.35 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.12);
  } catch (e) {}
};

// Kids Card Flip Sound
export const playCardFlipSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.06);
    gain.gain.setValueAtTime(0.18 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.07);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.07);
  } catch (e) {}
};

// Toddler & Kids Bubble Pop Sound (Delightful high water pop)
export const playBubblePopSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(700, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1400, ctx.currentTime + 0.04);
    osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.09);
    gain.gain.setValueAtTime(0.25 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  } catch (e) {}
};

// Little Star Collect Chime (Twinkling bell tones)
export const playStarCollectSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const notes = [1046.50, 1318.51, 1567.98, 2093.00];
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ctx.currentTime + idx * 0.06;
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.18 * globalSoundVolume, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.2);
    });
  } catch (e) {}
};

// Animal Match Cheer Sound (Warm cheerful cartoon victory)
export const playAnimalCheerSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const freqs = [523.25, 659.25, 783.99, 1046.50];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ctx.currentTime + i * 0.08;
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(f, t);
      gain.gain.setValueAtTime(0.22 * globalSoundVolume, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.25);
    });
  } catch (e) {}
};

// Rocket Thruster Roar (Low frequency rumble with rising pitch)
export const playRocketThrusterSound = (duration = 0.8) => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(65, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(140, ctx.currentTime + duration * 0.7);
    osc.frequency.exponentialRampToValueAtTime(45, ctx.currentTime + duration);

    gain.gain.setValueAtTime(0.35 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch (e) {}
};

// Chemistry Lab Bubbling Sound
export const playBubblingSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const pitches = [500, 650, 420, 780, 580];
    pitches.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ctx.currentTime + idx * 0.07;
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      osc.frequency.exponentialRampToValueAtTime(freq * 1.5, t + 0.05);

      gain.gain.setValueAtTime(0.15 * globalSoundVolume, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.08);
    });
  } catch (e) {}
};

// Sci-Fi Space Launch Chime
export const playLaunchChime = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const freqs = [392.00, 523.25, 659.25, 783.99, 1046.50, 1318.51];
    freqs.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ctx.currentTime + idx * 0.09;
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.2 * globalSoundVolume, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.3);
    });
  } catch (e) {}
};

// Cosmic Radar Sensor Ping
export const playCosmicRadarBeep = (freq = 987.77) => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(freq * 1.5, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.22 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.22);
  } catch (e) {}
};

// Paleontological Fossil Chisel & Brush Excavation Sound
export const playExcavationChiselSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    // Crisp stone tap + whisk
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(1200 + Math.random() * 600, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.25 * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.09);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.09);
  } catch (e) {}
};

// Drumroll for draws
export const playRollSound = (durationMs = 2400) => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    let interval = 80;
    const startTime = Date.now();

    const triggerNext = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= durationMs) return;

      playTickSound(400 + Math.random() * 200, 0.05);

      interval = Math.max(30, 80 - Math.floor((elapsed / durationMs) * 50));
      setTimeout(triggerNext, interval);
    };

    triggerNext();
  } catch (e) {
    console.debug('Drumroll audio error', e);
  }
};

// Countdown beep
export const playCountdownBeep = (isFinal = false) => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = isFinal ? 'sawtooth' : 'sine';
    osc.frequency.setValueAtTime(isFinal ? 880 : 440, ctx.currentTime);

    gain.gain.setValueAtTime((isFinal ? 0.25 : 0.15) * globalSoundVolume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + (isFinal ? 0.35 : 0.15));

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + (isFinal ? 0.35 : 0.15));
  } catch (e) {
    console.debug('Countdown beep audio error', e);
  }
};

// ==========================================
// 🎵 BACKGROUND MUSIC ENGINE
// ==========================================

export const MUSIC_THEMES = [
  { id: 'arcade_casino', name: '🎰 Vegas Casino Arcade', description: 'Upbeat jazzy casino energy with bright melodic electro chords' },
  { id: 'carnival_festive', name: '🎪 Festive Carnival Bonanza', description: 'Playful bouncy fairground melody ideal for retail events' },
  { id: 'electronic_rush', name: '⚡ Synthwave Rush', description: 'Modern futuristic pulsing electro arcade groove' },
  { id: 'ambient_chill', name: '✨ Elegant Ambient Lounge', description: 'Relaxing sophisticated background chords' },
  { id: 'custom', name: '🔗 Custom Audio Stream', description: 'Play your own audio/MP3 via direct URL' },
  { id: 'none', name: '🔇 Mute (No Music)', description: 'Disable background music completely' }
];

export const startBackgroundMusic = (theme = 'arcade_casino', volume = 0.35, customUrl = '') => {
  stopBackgroundMusic();
  if (theme === 'none') return;

  isBgmActive = true;
  globalMusicVolume = volume;

  // Custom URL Mode
  if (theme === 'custom' && customUrl && customUrl.trim()) {
    try {
      currentBgmAudio = new Audio(customUrl.trim());
      currentBgmAudio.loop = true;
      currentBgmAudio.volume = volume;
      currentBgmAudio.play().catch(e => {
        console.debug('Autoplay policy prevented audio play', e);
      });
      return;
    } catch (e) {
      console.error('Failed to load custom audio URL', e);
    }
  }

  // Native Web Audio Synthesized Looping Music Themes
  const ctx = getAudioContext();
  if (!ctx) return;

  proceduralBgmGain = ctx.createGain();
  proceduralBgmGain.gain.setValueAtTime(volume * 0.14, ctx.currentTime);
  proceduralBgmGain.connect(ctx.destination);

  let step = 0;
  let tempoMs = 180; // Default tempo

  // Preset Pattern Frequencies (Hz)
  let bassNotes = [];
  let leadNotes = [];

  if (theme === 'arcade_casino') {
    tempoMs = 160;
    // Funky Vegas C minor / Eb major groove
    bassNotes = [130.81, 130.81, 155.56, 174.61, 130.81, 196.00, 174.61, 155.56];
    leadNotes = [523.25, 659.25, 783.99, 659.25, 523.25, 783.99, 880.00, 1046.50];
  } else if (theme === 'carnival_festive') {
    tempoMs = 210;
    // Bouncy polka carnival 3/4 feel
    bassNotes = [196.00, 146.83, 146.83, 220.00, 164.81, 164.81];
    leadNotes = [783.99, 880.00, 987.77, 1046.50, 987.77, 880.00];
  } else if (theme === 'electronic_rush') {
    tempoMs = 135;
    // 80s Synthwave bassline
    bassNotes = [110.00, 110.00, 130.81, 146.83, 110.00, 110.00, 164.81, 146.83];
    leadNotes = [440.00, 523.25, 587.33, 659.25, 440.00, 587.33, 659.25, 783.99];
  } else {
    // Ambient Chill
    tempoMs = 400;
    bassNotes = [130.81, 164.81, 196.00, 174.61];
    leadNotes = [523.25, 659.25, 783.99, 698.46];
  }

  proceduralBgmInterval = setInterval(() => {
    if (!isBgmActive || !audioCtx) return;
    try {
      const now = audioCtx.currentTime;

      // Bass synth note
      if (bassNotes.length > 0) {
        const bassFreq = bassNotes[step % bassNotes.length];
        const bOsc = audioCtx.createOscillator();
        const bGain = audioCtx.createGain();
        bOsc.type = 'triangle';
        bOsc.frequency.setValueAtTime(bassFreq, now);

        bGain.gain.setValueAtTime(0.08 * globalMusicVolume, now);
        bGain.gain.exponentialRampToValueAtTime(0.001, now + (tempoMs / 1000) * 0.9);

        bOsc.connect(bGain);
        bGain.connect(proceduralBgmGain);
        bOsc.start(now);
        bOsc.stop(now + (tempoMs / 1000));
      }

      // Lead melody note (every 2 steps or on accents)
      if (leadNotes.length > 0 && (step % 2 === 0 || Math.random() > 0.4)) {
        const leadFreq = leadNotes[step % leadNotes.length];
        const lOsc = audioCtx.createOscillator();
        const lGain = audioCtx.createGain();
        lOsc.type = 'sine';
        lOsc.frequency.setValueAtTime(leadFreq, now);

        lGain.gain.setValueAtTime(0.05 * globalMusicVolume, now);
        lGain.gain.exponentialRampToValueAtTime(0.001, now + (tempoMs / 1000) * 0.7);

        lOsc.connect(lGain);
        lGain.connect(proceduralBgmGain);
        lOsc.start(now);
        lOsc.stop(now + (tempoMs / 1000));
      }

      step++;
    } catch (e) {
      console.debug('BGM step error', e);
    }
  }, tempoMs);
};

export const stopBackgroundMusic = () => {
  isBgmActive = false;
  if (proceduralBgmInterval) {
    clearInterval(proceduralBgmInterval);
    proceduralBgmInterval = null;
  }
  if (proceduralBgmGain && audioCtx) {
    try {
      proceduralBgmGain.disconnect();
    } catch (e) {}
    proceduralBgmGain = null;
  }
  if (currentBgmAudio) {
    try {
      currentBgmAudio.pause();
      currentBgmAudio.currentTime = 0;
    } catch (e) {}
    currentBgmAudio = null;
  }
};

export const isMusicPlaying = () => isBgmActive;
